/**
 * In this file we augment the user and account types
 * with additional properties that are useful in a centralized location.
 */
import { AppBundle } from '@/utils/app-bundles';
import { getLimitValue } from '@/utils/plan-features';
import { UserMasqPermission } from '@customerio/hydra-permissions/masq';
import { snakeCase } from 'lodash-es';
import type { Account, AugmentedAccount, AugmentedUser, User } from './types';
import { PlanFeature } from './types';

/**
 * Augments the account object with plan and product information.
 */
export function augmentAccount(account: Account): AugmentedAccount {
  // Enables premium label for premium trial users
  const premiumLabelEnabled = account.premium_trial === true && account.premium === false;
  const gatedFeatures = account.gated_features ?? {};

  const planFeatures: Partial<AugmentedAccount['planFeatures']> = {};
  for (const key of Object.values(PlanFeature)) {
    const feature = gatedFeatures[snakeCase(key)];
    const limit = getLimitValue(feature);
    const enabled = feature !== undefined; // if a feature is present on the gatedFeatures object, it is enabled.
    planFeatures[key] = {
      enabled,
      showPremiumLabel: !enabled || premiumLabelEnabled,
      limit,
    };
  }

  const activeProducts = Object.values(AppBundle).reduce(
    (activeProductsMap, app) => {
      activeProductsMap[app] = account?.product_activations?.includes(app) ?? false;
      return activeProductsMap;
    },
    {} as Record<AppBundle, boolean>,
  );

  const now = Math.floor(Date.now() / 1000); // Current timestamp in seconds
  const supportTeamAccessEnabled = (account.access_granted_until ?? 0) > now;
  const successTeamAccessEnabled =
    (account.cs_access_granted_until ?? 0) === -1 || (account.cs_access_granted_until ?? 0) > now;

  return {
    ...account,
    activeProducts,
    planFeatures: planFeatures as AugmentedAccount['planFeatures'],
    support_team_access_enabled: supportTeamAccessEnabled,
    success_team_access_enabled: successTeamAccessEnabled,
  };
}

/**
 * Augments the user object with masq permissions.
 */
export function augmentUser(user: User): AugmentedUser {
  const masqPermissions = user?.masq_permissions ?? UserMasqPermission.NoMasqAccess;

  const isSuperUser = masqPermissions === UserMasqPermission.SuperUser;
  const canReadWriteMasq = masqPermissions === UserMasqPermission.ReadWrite;
  const canReadOnlyMasq =
    masqPermissions === UserMasqPermission.ReadOnly || masqPermissions === UserMasqPermission.Redacted;
  const hasReadMasqAccess = isSuperUser || canReadWriteMasq || canReadOnlyMasq;

  return {
    ...user,
    name: `${user.first_name} ${user.last_name}`.trim(),
    isSuperUser,
    canReadOnlyMasq,
    canReadWriteMasq,
    noMasqAccess: masqPermissions === UserMasqPermission.NoMasqAccess || masqPermissions == null,
    masqLevelNotSet: masqPermissions === UserMasqPermission.MasqLevelNotSet,
    hasReadMasqAccess,
  };
}
